import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import type { LandingTab } from "@/hooks/useLandingTab";

interface ToggleItem {
  id: string;
  label: string;
  shortLabel: string;
  type: "tab" | "link";
  href?: string;
}

const ITEMS: ToggleItem[] = [
  { id: "ai-department", label: "AI Platform", shortLabel: "Platform", type: "tab" },
  { id: "distributors", label: "Distributors", shortLabel: "Distrib.", type: "link", href: "/distributors" },
  { id: "ads", label: "AI Ads", shortLabel: "Ads", type: "link", href: "/ads" },
];

interface LandingToggleProps {
  activeTab: LandingTab;
  onTabChange: (tab: LandingTab) => void;
}

export function LandingToggle({ activeTab, onTabChange }: LandingToggleProps) {
  const navigate = useNavigate();

  return (
    <div
      className="fixed top-14 left-0 right-0 z-40 transition-all duration-300"
      style={{
        background: "hsl(240 25% 4% / 0.85)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        borderBottom: "1px solid hsl(var(--border) / 0.15)",
      }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col items-center justify-center py-2 gap-1.5">
        <span className="text-[9px] font-semibold uppercase tracking-[0.25em] text-primary/50">
          What we offer
        </span>
        <div className="flex items-center gap-3">
          {/* Main product toggle */}
          <div
            className="relative flex items-center rounded-full p-1"
            style={{
              background: "hsl(240 20% 10% / 0.95)",
              border: "1px solid hsl(var(--border) / 0.4)",
              boxShadow: "0 2px 12px hsl(240 33% 2% / 0.5), inset 0 1px 0 hsl(0 0% 100% / 0.03)",
            }}
          >
            {ITEMS.map((item) => {
              const isActive = item.type === "tab" && activeTab === item.id;

              return (
                <button
                  key={item.id}
                  onClick={() => {
                    if (item.type === "link" && item.href) {
                      navigate(item.href);
                    } else {
                      onTabChange(item.id as LandingTab);
                    }
                  }}
                  className="relative px-4 sm:px-6 py-1.5 text-xs sm:text-sm font-medium rounded-full transition-colors duration-200 whitespace-nowrap"
                  style={{
                    color: isActive ? "hsl(0 0% 100%)" : "hsl(0 0% 100% / 0.4)",
                  }}
                >
                  {isActive && (
                    <motion.div
                      layoutId="landing-tab-indicator"
                      className="absolute inset-0 rounded-full"
                      style={{
                        background: "linear-gradient(135deg, hsl(217 91% 60% / 0.2), hsl(258 90% 66% / 0.15))",
                        border: "1px solid hsl(217 91% 60% / 0.35)",
                        boxShadow: "0 0 12px hsl(217 91% 60% / 0.1)",
                      }}
                      transition={{ type: "spring", stiffness: 400, damping: 30 }}
                    />
                  )}
                  <span className="relative z-10 hidden sm:inline">{item.label}</span>
                  <span className="relative z-10 sm:hidden">{item.shortLabel}</span>
                </button>
              );
            })}
          </div>

          {/* Defense — separate business vertical */}
          <button
            onClick={() => navigate("/defense")}
            className="hidden sm:flex items-center gap-2 px-4 py-1.5 rounded-full text-[12px] font-medium transition-all hover:brightness-125 whitespace-nowrap"
            style={{
              background: "rgba(166,242,204,0.08)",
              border: "1px solid rgba(166,242,204,0.2)",
              color: "#a6f2cc",
            }}
          >
            <span className="w-1 h-1 rounded-full bg-[#a6f2cc] animate-pulse" />
            Defense & Security
          </button>
        </div>
      </div>
    </div>
  );
}
